import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, RefreshCw, Trash2, Plus, Users, BarChart3, ExternalLink, Power, Wrench, Gauge, LayoutDashboard, IndianRupee, BadgeCheck, FileText, Settings, CreditCard, Inbox, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BusinessTab, RevenueTab, SubscribersTab, ContentTab } from "@/components/AdminDashboards";
import { toast } from "sonner";

const ADMIN_LANGS = [
  { value: "english", label: "English" }, { value: "hindi", label: "Hindi" },
  { value: "hinglish", label: "Hinglish" },
];

export default function Admin() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [videos, setVideos] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterTopic, setFilterTopic] = useState("all");
  const [filterLang, setFilterLang] = useState("all");
  const [loading, setLoading] = useState(true);
  const [newVid, setNewVid] = useState({ youtube_id: "", topic_slug: "", language: "english" });
  const [adding, setAdding] = useState(false);
  const [tools, setTools] = useState([]);
  const [toolCats, setToolCats] = useState([]);
  const [newTool, setNewTool] = useState({ slug: "", name: "", tagline: "", category: "chat", color: "lime", search: "" });
  const [addingTool, setAddingTool] = useState(false);
  const [usage, setUsage] = useState(null);
  const [overview, setOverview] = useState(null);
  const [subscribers, setSubscribers] = useState(null);
  const [payments, setPayments] = useState(null);
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingPaywall, setSavingPaywall] = useState(false);
  const [toolReqs, setToolReqs] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);

  const loadCore = useCallback(async () => {
    // resilient: a single transient failure (e.g. YouTube quota) must not blank the whole panel
    const calls = {
      topics: "/admin/topics", users: "/admin/users", tools: "/admin/tools",
      usage: "/admin/youtube-usage", overview: "/admin/overview",
      subscribers: "/admin/subscribers", payments: "/admin/payments", content: "/admin/content",
      settings: "/admin/settings", toolreqs: "/admin/tool-requests",
    };
    const keys = Object.keys(calls);
    const results = await Promise.allSettled(keys.map((k) => api.get(calls[k])));
    const out = {};
    results.forEach((r, i) => { if (r.status === "fulfilled") out[keys[i]] = r.value.data; });
    if (out.topics) setTopics(out.topics);
    if (out.users) setUsers(out.users);
    if (out.tools) { setTools(out.tools.tools); setToolCats(out.tools.categories); }
    if (out.usage) setUsage(out.usage);
    if (out.overview) setOverview(out.overview);
    if (out.subscribers) setSubscribers(out.subscribers);
    if (out.payments) setPayments(out.payments);
    if (out.content) setContent(out.content);
    if (out.settings) setSettings(out.settings);
    if (out.toolreqs) setToolReqs(out.toolreqs);
  }, []);

  const loadVideos = useCallback(async () => {
    const params = {};
    if (filterTopic !== "all") params.topic_slug = filterTopic;
    if (filterLang !== "all") params.language = filterLang;
    const { data } = await api.get("/admin/videos", { params });
    setVideos(data);
  }, [filterTopic, filterLang]);

  useEffect(() => {
    (async () => {
      try {
        await loadCore();
        await loadVideos();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) loadVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTopic, filterLang]);

  const toggleStatus = async (v) => {
    const status = v.status === "active" ? "dead" : "active";
    await api.put(`/admin/videos/${v.video_id}`, { status });
    toast.success(`Marked ${status}`);
    loadVideos();
  };

  const remove = async (v) => {
    await api.delete(`/admin/videos/${v.video_id}`);
    toast.success("Deleted");
    loadVideos();
    loadCore();
  };

  const curate = async (slug) => {
    await api.post("/admin/curate", { topic_slug: slug, force: true });
    toast.success("Curation started in the background. Refresh in a minute.");
  };

  const [curatingAll, setCuratingAll] = useState(false);
  const curatePlatform = async () => {
    setCuratingAll(true);
    try {
      const { data } = await api.post("/admin/curate-platform");
      toast.success(`Queued ${(data.enqueued_tool_lessons || 0) + (data.enqueued_core_topics || 0)} lessons. The worker fills them within the daily YouTube budget.`);
      loadCore();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCuratingAll(false);
    }
  };

  const addVideo = async () => {
    if (!newVid.youtube_id || !newVid.topic_slug) {
      toast.error("Enter a YouTube ID and pick a topic");
      return;
    }
    setAdding(true);
    try {
      await api.post("/admin/videos", newVid);
      toast.success("Video added");
      setNewVid({ youtube_id: "", topic_slug: "", language: "english" });
      loadVideos();
      loadCore();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const addTool = async () => {
    if (!newTool.slug || !newTool.name) {
      toast.error("Enter a slug and a name");
      return;
    }
    setAddingTool(true);
    try {
      await api.post("/admin/tools", newTool);
      toast.success("Tool added to the catalogue");
      setNewTool({ slug: "", name: "", tagline: "", category: "chat", color: "lime", search: "" });
      loadCore();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAddingTool(false);
    }
  };

  const removeTool = async (slug) => {
    await api.delete(`/admin/tools/${slug}`);
    toast.success("Tool removed");
    loadCore();
  };

  const togglePaywall = async (next) => {
    setSavingPaywall(true);
    try {
      const { data } = await api.post("/admin/settings/paywall", { enabled: next });
      setSettings(data);
      toast.success(next
        ? "Payments turned ON — a subscription is now required."
        : "Payments turned OFF — all content is free for everyone.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingPaywall(false);
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/send-digest", {});
      toast.success(`Digest: ${data.sent ?? 0} sent of ${data.attempted ?? 0} due`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingDigest(false);
    }
  };

  const [sendingRecovery, setSendingRecovery] = useState(false);
  const sendPaymentReminders = async () => {
    setSendingRecovery(true);
    try {
      const { data } = await api.post("/admin/payment-recovery?include_old=true&resend=true");
      toast.success(`Payment reminders: ${data.sent_first ?? 0} first + ${data.sent_followup ?? 0} follow-ups sent`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingRecovery(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PageTransition className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Admin · Command center</h1>
            <p className="mt-2 text-muted-foreground">Your business, revenue, subscribers, content & curation — all in one place.</p>
          </div>
          <button data-testid="admin-survey-results" onClick={() => navigate("/admin/results")}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors">
            <BarChart3 className="h-4 w-4" /> PMF Survey results
          </button>
        </div>

        <Tabs defaultValue="business" className="mt-8">
          <TabsList data-testid="admin-tabs" className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="settings" data-testid="tab-settings"><Settings className="h-4 w-4 mr-1.5" /> Settings</TabsTrigger>
            <TabsTrigger value="business" data-testid="tab-business"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Business</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="tab-revenue"><IndianRupee className="h-4 w-4 mr-1.5" /> Revenue</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subscribers"><BadgeCheck className="h-4 w-4 mr-1.5" /> Subscribers</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content"><FileText className="h-4 w-4 mr-1.5" /> Content</TabsTrigger>
            <TabsTrigger value="library" data-testid="tab-library">Library</TabsTrigger>
            <TabsTrigger value="topics" data-testid="tab-topics">Topics</TabsTrigger>
            <TabsTrigger value="tools" data-testid="tab-tools">AI Tools</TabsTrigger>
            <TabsTrigger value="quota" data-testid="tab-quota">API Quota</TabsTrigger>
            <TabsTrigger value="learners" data-testid="tab-learners">Learners</TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="mt-6" data-testid="settings-tab">
            <div className="rounded-2xl border border-border bg-card p-6 max-w-2xl">
              <div className="flex items-start gap-3">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-primary/10 text-primary shrink-0">
                  <CreditCard className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-lg">Payments &amp; paywall</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    One click to turn the subscription requirement on or off across the whole app — takes effect instantly, no redeploy.
                  </p>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between gap-4 rounded-xl border border-border bg-background p-4">
                <div className="min-w-0">
                  <p className="font-semibold flex items-center gap-2">
                    Payments
                    {settings && (
                      <span data-testid="paywall-state-badge"
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${settings.paywall_enabled ? "bg-success/15 text-success" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}>
                        {settings.paywall_enabled ? "ON · subscription required" : "OFF · everything free"}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {settings?.paywall_enabled
                      ? "Users must subscribe (Razorpay) to access lessons, tools, and Pro features."
                      : "All content is unlocked for every signed-in user. Razorpay checkout is paused."}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {savingPaywall && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  <Switch
                    data-testid="paywall-toggle"
                    checked={!!settings?.paywall_enabled}
                    disabled={!settings || savingPaywall}
                    onCheckedChange={togglePaywall}
                  />
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Admins always bypass the paywall regardless of this setting.
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 max-w-2xl mt-6" data-testid="digest-card">
              <div className="flex items-start gap-3">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-primary/10 text-primary shrink-0">
                  <Mail className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading font-semibold text-lg">Weekly progress digest</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Runs automatically every day and emails each learner at most once a week. Until your domain is
                    verified in Resend, delivery only works to your own founder email.
                  </p>
                </div>
              </div>
              <Button data-testid="send-digest-btn" onClick={sendDigest} disabled={sendingDigest} variant="outline" className="mt-4 rounded-xl">
                {sendingDigest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send due digests now
              </Button>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 max-w-2xl mt-6" data-testid="recovery-card">
              <div className="flex items-start gap-3">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-primary/10 text-primary shrink-0">
                  <IndianRupee className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading font-semibold text-lg">Payment reminder emails</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Runs automatically: anyone whose payment fails or is abandoned gets a retry email after 5 minutes
                    and a follow-up after 24 hours. New signups who haven't paid get a "why AI Pathshaalaa" email
                    10 minutes after signup. This button <b>re-sends the reminder to EVERY currently stuck payment</b>
                    (any age, even if reminded before) — paid customers are always excluded.
                  </p>
                </div>
              </div>
              <Button data-testid="send-recovery-btn" onClick={sendPaymentReminders} disabled={sendingRecovery} variant="outline" className="mt-4 rounded-xl">
                {sendingRecovery ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send payment reminders now (incl. old)
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="business" className="mt-6"><BusinessTab data={overview} /></TabsContent>
          <TabsContent value="revenue" className="mt-6"><RevenueTab overview={overview} payments={payments} /></TabsContent>
          <TabsContent value="subscribers" className="mt-6"><SubscribersTab data={subscribers} /></TabsContent>
          <TabsContent value="content" className="mt-6"><ContentTab data={content} /></TabsContent>

          {/* LIBRARY */}
          <TabsContent value="library" className="mt-6">
            {/* add video */}
            <div className="rounded-2xl border border-border bg-card p-5 mb-6">
              <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> Add a video manually</h3>
              <div className="mt-4 flex flex-col md:flex-row gap-3">
                <Input data-testid="add-youtube-id" placeholder="YouTube video ID (e.g. dQw4w9WgXcQ)" value={newVid.youtube_id} onChange={(e) => setNewVid({ ...newVid, youtube_id: e.target.value })} className="rounded-xl" />
                <Select value={newVid.topic_slug} onValueChange={(val) => setNewVid({ ...newVid, topic_slug: val })}>
                  <SelectTrigger className="rounded-xl md:w-64" data-testid="add-topic-select"><SelectValue placeholder="Topic" /></SelectTrigger>
                  <SelectContent>
                    {topics.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={newVid.language} onValueChange={(val) => setNewVid({ ...newVid, language: val })}>
                  <SelectTrigger className="rounded-xl md:w-36" data-testid="add-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ADMIN_LANGS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="add-video-btn" onClick={addVideo} disabled={adding} className="rounded-xl bg-primary hover:bg-primary/90">
                  {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                </Button>
              </div>
            </div>

            {/* filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              <Select value={filterTopic} onValueChange={setFilterTopic}>
                <SelectTrigger className="rounded-xl w-56" data-testid="filter-topic"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All topics</SelectItem>
                  {topics.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterLang} onValueChange={setFilterLang}>
                <SelectTrigger className="rounded-xl w-40" data-testid="filter-lang"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All languages</SelectItem>
                  {ADMIN_LANGS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground self-center">{videos.length} videos</span>
            </div>

            <div className="space-y-2" data-testid="admin-video-list">
              {videos.map((v) => (
                <div key={v.video_id} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-4">
                  <img src={v.thumbnail} alt="" className="h-14 w-24 object-cover rounded-lg shrink-0 bg-secondary" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{v.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {v.channel} · {v.topic_slug} · {v.language} · score {v.score}
                    </p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${v.status === "active" ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"}`}>
                    {v.status}
                  </span>
                  <a href={`https://youtu.be/${v.youtube_id}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground shrink-0">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <Button size="icon" variant="ghost" data-testid={`toggle-${v.video_id}`} onClick={() => toggleStatus(v)} title="Toggle active/dead">
                    <Power className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" data-testid={`delete-${v.video_id}`} onClick={() => remove(v)} className="text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {videos.length === 0 && <p className="text-muted-foreground text-sm py-8 text-center">No videos for this filter.</p>}
            </div>
          </TabsContent>

          {/* TOPICS */}
          <TabsContent value="topics" className="mt-6">
            <div className="space-y-2" data-testid="admin-topic-list">
              {topics.map((t) => (
                <div key={t.slug} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-4">
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-primary/10 text-primary font-bold font-heading shrink-0">{t.level}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{t.title}</p>
                    <p className="text-xs text-muted-foreground">{t.level_label} · {t.video_count} videos</p>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`curate-${t.slug}`} onClick={() => curate(t.slug)} className="rounded-full shrink-0">
                    <RefreshCw className="h-4 w-4" /> Re-curate
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* AI TOOLS */}
          <TabsContent value="tools" className="mt-6">
            {toolReqs.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-5 mb-6" data-testid="tool-requests-panel">
                <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                  <Inbox className="h-4 w-4 text-primary" /> Requested by learners
                  <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-primary/10 text-primary">{toolReqs.length}</span>
                </h3>
                <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                  {toolReqs.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{r.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{r.email}{r.note ? ` — “${r.note}”` : ""}</p>
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0">{(r.created_at || "").slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="rounded-2xl border border-border bg-card p-5 mb-6">
              <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><Wrench className="h-4 w-4 text-primary" /> Add an AI tool to the catalogue</h3>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Input data-testid="tool-slug" placeholder="Slug (e.g. claude-code)" value={newTool.slug} onChange={(e) => setNewTool({ ...newTool, slug: e.target.value })} className="rounded-xl" />
                <Input data-testid="tool-name" placeholder="Name (e.g. Claude Code)" value={newTool.name} onChange={(e) => setNewTool({ ...newTool, name: e.target.value })} className="rounded-xl" />
                <Input data-testid="tool-search" placeholder="Search term (optional)" value={newTool.search} onChange={(e) => setNewTool({ ...newTool, search: e.target.value })} className="rounded-xl" />
                <Input data-testid="tool-tagline" placeholder="One-line tagline" value={newTool.tagline} onChange={(e) => setNewTool({ ...newTool, tagline: e.target.value })} className="rounded-xl sm:col-span-2" />
                <Select value={newTool.category} onValueChange={(val) => setNewTool({ ...newTool, category: val })}>
                  <SelectTrigger className="rounded-xl" data-testid="tool-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {toolCats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={newTool.color} onValueChange={(val) => setNewTool({ ...newTool, color: val })}>
                  <SelectTrigger className="rounded-xl" data-testid="tool-color"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["lime", "pink", "cyber", "tangerine"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="add-tool-btn" onClick={addTool} disabled={addingTool} className="rounded-xl bg-primary hover:bg-primary/90">
                  {addingTool ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Add tool</>}
                </Button>
              </div>
            </div>

            <div className="space-y-2" data-testid="admin-tool-list">
              {tools.map((t) => (
                <div key={t.slug} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-4">
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-primary/10 text-primary shrink-0 text-xs font-bold font-heading uppercase">{t.category?.slice(0, 3)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{t.name} <span className="text-xs text-muted-foreground">/{t.slug}</span></p>
                    <p className="text-xs text-muted-foreground truncate">{t.tagline} · {t.video_count || 0} cached lessons{t.core_topic ? ` · core: ${t.core_topic}` : ""}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${t.status === "active" ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"}`}>{t.status}</span>
                  <Button size="icon" variant="ghost" data-testid={`delete-tool-${t.slug}`} onClick={() => removeTool(t.slug)} className="text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {tools.length === 0 && <p className="text-muted-foreground text-sm py-8 text-center">No tools yet.</p>}
            </div>
          </TabsContent>

          {/* API QUOTA */}
          <TabsContent value="quota" className="mt-6" data-testid="quota-report">
            {!usage ? (
              <div className="py-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : (
              <div className="space-y-6">
                <div className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" /> YouTube Data API — today ({usage.today.day}, US/Pacific)</h3>
                    <span className="text-xs text-muted-foreground">key {usage.key}</span>
                  </div>
                  <div className="mt-3 flex items-end gap-2">
                    <span data-testid="quota-today-units" className="text-3xl font-bold font-heading">{usage.today.units.toLocaleString()}</span>
                    <span className="text-muted-foreground mb-1">/ {usage.quota_limit.toLocaleString()} units · {usage.today.searches} searches</span>
                  </div>
                  <div className="mt-2 h-3 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${Math.min(100, (usage.today.units / usage.quota_limit) * 100)}%` }} />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">{usage.today.remaining.toLocaleString()} units remaining today.</p>
                </div>

                {usage.curation_queue && (
                  <div className="rounded-2xl border border-border bg-card p-5">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><Wrench className="h-4 w-4 text-primary" /> Curation queue</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          <span data-testid="queue-pending" className="font-bold text-foreground">{usage.curation_queue.pending}</span> pending ·{" "}
                          {usage.curation_queue.done} done · {usage.curation_queue.error} error
                        </p>
                      </div>
                      <Button data-testid="curate-platform-btn" onClick={curatePlatform} disabled={curatingAll} className="rounded-xl">
                        {curatingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Curate entire platform
                      </Button>
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">
                      Queues every roadmap topic + tool lesson that has no video yet. The background worker fills them
                      gradually within the daily YouTube search limit (100 searches/day) — bigger libraries take a few days.
                    </p>
                  </div>
                )}

                <div className="grid sm:grid-cols-3 gap-3">
                  {["user_action", "background_job", "admin_action"].map((cat) => {
                    const u = usage.today.by_category[cat]?.units || 0;
                    const isLeak = cat === "user_action";
                    return (
                      <div key={cat} className={`rounded-2xl border p-4 ${isLeak ? (u > 0 ? "border-destructive/40 bg-destructive/5" : "border-success/40 bg-success/5") : "border-border bg-card"}`}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{cat.replace("_", " ")}</p>
                        <p data-testid={`quota-cat-${cat}`} className="text-2xl font-bold font-heading mt-1">{u.toLocaleString()}<span className="text-sm text-muted-foreground font-normal"> units</span></p>
                        {isLeak && <p className={`text-xs mt-1 ${u > 0 ? "text-destructive" : "text-success"}`}>{u > 0 ? "⚠ user requests are spending quota" : "✓ user requests cost 0 quota"}</p>}
                      </div>
                    );
                  })}
                </div>

                <div className="rounded-2xl border border-border bg-card p-5">
                  <h3 className="font-heading font-semibold text-base mb-3">Top sources — today</h3>
                  {usage.today.by_source.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No YouTube API calls recorded today.</p>
                  ) : (
                    <div className="space-y-2">
                      {usage.today.by_source.map((r) => (
                        <div key={r.source} data-testid={`quota-source-${r.source}`} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5">
                          <span className="font-medium">{r.source}</span>
                          <span className="text-muted-foreground">{r.calls} calls · <span className="font-semibold text-foreground">{r.units.toLocaleString()} units</span></span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-border bg-card p-5">
                  <h3 className="font-heading font-semibold text-base mb-3">Last 7 days — {usage.last_7_days.units.toLocaleString()} units · {usage.last_7_days.searches} searches</h3>
                  <div className="space-y-1.5">
                    {usage.last_7_days.daily.map((d) => (
                      <div key={d.day} className="flex items-center gap-3 text-sm">
                        <span className="w-24 text-muted-foreground font-mono text-xs">{d.day}</span>
                        <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                          <div className="h-full bg-primary/70" style={{ width: `${Math.min(100, (d.units / usage.quota_limit) * 100)}%` }} />
                        </div>
                        <span className="w-28 text-right text-muted-foreground">{d.units.toLocaleString()}u · {d.searches}s</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* LEARNERS */}
          <TabsContent value="learners" className="mt-6">
            <div className="space-y-2" data-testid="admin-user-list">
              {users.map((u) => (
                <div key={u.user_id} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
                  <span className="h-9 w-9 rounded-full bg-secondary grid place-items-center font-heading font-bold text-muted-foreground shrink-0">
                    {(u.name || u.email)[0]?.toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{u.name} {u.role === "admin" && <span className="text-xs text-primary">(admin)</span>}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email} · via {u.auth_provider}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{u.has_path ? "has path" : "no path"}</span>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </PageTransition>
    </div>
  );
}
